import { config } from 'src/config'
import {
  IdpError,
  type AuthProviderList,
  type ConsentUrlResponse,
  type DiscoveryResponse,
  type FederatedProvider,
  type Session,
  type TokenResponse,
} from './types'

/**
 * Typed fetch client for the Passgenau IdP.
 *
 * Every response body is either the documented success schema or the `Error`
 * schema, so failures are normalised into `IdpError` here and callers never
 * inspect a raw Response.
 */

const url = (path: string): string => `${config.idp.baseUrl}${path}`

/** Turn a non-2xx response into an IdpError, tolerating a non-JSON body. */
const toError = async (response: Response): Promise<IdpError> => {
  let code = 'unknown_error'
  let message = `The identity service returned ${response.status}.`
  let requestId: string | undefined

  try {
    const body: unknown = await response.json()
    if (body && typeof body === 'object') {
      const error = body as Record<string, unknown>
      if (typeof error.code === 'string') code = error.code
      if (typeof error.message === 'string') message = error.message
      if (typeof error.requestId === 'string') requestId = error.requestId
    }
  } catch {
    // A gateway timeout or a proxy error page is not JSON. The status-derived
    // message above is the best available.
  }

  return new IdpError(response.status, code, message, requestId)
}

const request = async <T>(
  path: string,
  init: RequestInit & { accessToken?: string } = {}
): Promise<T> => {
  const { accessToken, headers, ...rest } = init

  let response: Response
  try {
    response = await fetch(url(path), {
      ...rest,
      headers: {
        ...(rest.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
    })
  } catch (cause) {
    // A network failure, DNS failure or CORS rejection never reaches the
    // service, so there is no status and no code to report.
    console.error('Could not reach the identity service', cause)
    throw new IdpError(0, 'network_error', 'Could not reach the identity service.')
  }

  if (!response.ok) throw await toError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

const post = <T>(path: string, body?: unknown, accessToken?: string): Promise<T> =>
  request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(accessToken ? { accessToken } : {}),
  })

/**
 * The platform's own upstream registrations, so the sign-in page can render its
 * buttons before anyone has typed an address. Providers the deployment names
 * but has not finished configuring come back with `enabled: false`.
 */
export const listAuthProviders = (): Promise<AuthProviderList> =>
  request<AuthProviderList>('/auth/provider')

/**
 * Report the connection an address belongs to. A domain with no connection of
 * its own is answered with the platform's default registrations rather than an
 * error, so this cannot be used to enumerate configured tenants.
 */
export const discoverLoginMethod = (email: string): Promise<DiscoveryResponse> =>
  post<DiscoveryResponse>('/auth/discovery', { email })

/**
 * Ask for the upstream consent URL. The returned `state` must be handed back
 * unchanged to `exchangeFederatedCode`.
 */
export const getConsentUrl = (
  provider: FederatedProvider,
  options: { connectionId?: string | null; loginHint?: string } = {}
): Promise<ConsentUrlResponse> =>
  post<ConsentUrlResponse>(`/auth/${provider}/url`, {
    ...(options.connectionId ? { connectionId: options.connectionId } : {}),
    ...(options.loginHint ? { loginHint: options.loginHint } : {}),
  })

/** Exchange the short-lived internal code delivered to the callback route. */
export const exchangeFederatedCode = (
  provider: FederatedProvider,
  state: string,
  code: string
): Promise<TokenResponse> => post<TokenResponse>(`/auth/${provider}/token`, { state, code })

/**
 * Rotate the refresh token.
 *
 * The presented token is consumed. Presenting one that has already been rotated
 * revokes the entire chain, so exactly one caller may be in here at a time —
 * see the lock in `refreshSession`.
 */
export const refreshTokens = (refreshToken: string): Promise<TokenResponse> =>
  post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken })

/** Revoke the refresh chain and clear the IdP's own browser session. */
export const endSession = (accessToken: string, refreshToken?: string): Promise<void> =>
  post<void>('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}, accessToken)

/** The profile and platform claims for a bearer token. */
export const getSession = (accessToken: string): Promise<Session> =>
  request<Session>('/auth/session', { accessToken })
